import React from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { FileDownload, Print as PrintIcon } from '@mui/icons-material';
import domtoimage from 'dom-to-image';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { toNumber } from '../utils/toNumber';

const exportableColumnKeys = [
  'category', 'name', 'description', 'parts', 'labor_hours', 'labor_rate',
  'labor_total', 'estimate_total', 'ro_status'
];

const VehicleHeader = ({ active_vehicle, groupedItems, setFlyoutOpen, filename }) => {
  const default_labor_rate = 100; // Fallback, ideally passed as prop if needed

  const handleExportDataToCSV = () => {
    const allItems = Object.values(groupedItems).flat();
    if (!allItems.length) return;

    const rowData = allItems.map((item) =>
      exportableColumnKeys.reduce((acc, key) => {
        if (key === 'labor_total') {
          acc[key] = (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate)).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = toNumber(item.parts) + (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate));
          const stored = toNumber(item.estimate_total) || 0;
          acc[key] = (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          acc[key] = item[key] || '';
        }
        return acc;
      }, {})
    );

    const csvConfig = mkConfig({
      fieldSeparator: ',',
      decimalSeparator: '.',
      useKeysAsHeaders: true,
      filename,
    });

    const csv = generateCsv(csvConfig)(rowData);
    download({ ...csvConfig, filename: `${filename}_${active_vehicle.stock}` })(csv);
  };

  const handleExportDataToPDF = () => {
    const allItems = Object.values(groupedItems).flat();
    if (!allItems.length) return;

    const doc = new jsPDF();
    const vehicleInfo = [
      [`VIN: ${active_vehicle.vin || 'N/A'}`],
      [`Stock: ${active_vehicle.stock || 'N/A'}`],
      [`Year: ${active_vehicle.year || 'N/A'}`],
      [`Make: ${active_vehicle.make || 'N/A'}`],
      [`Model: ${active_vehicle.model || 'N/A'}`],
    ];

    doc.text('Vehicle Information', 10, 10);
    autoTable(doc, { startY: 20, body: vehicleInfo });

    const tableData = allItems.map((item) =>
      exportableColumnKeys.map((key) => {
        if (key === 'labor_total') {
          return (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate)).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = toNumber(item.parts) + (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate));
          const stored = toNumber(item.estimate_total) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          return item[key] || '';
        }
      })
    );

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [exportableColumnKeys.map(key => key.replace(/_/g, ' ').toUpperCase())],
      body: tableData,
    });

    doc.save(`${filename}_${active_vehicle.stock}.pdf`);
  };

  const handlePrint = () => {
    const allItems = Object.values(groupedItems).flat();
    if (!allItems.length) return;

    const doc = new jsPDF();
    const vehicleInfo = [
      [`VIN: ${active_vehicle.vin || 'N/A'}`],
      [`Stock: ${active_vehicle.stock || 'N/A'}`],
      [`Year: ${active_vehicle.year || 'N/A'}`],
      [`Make: ${active_vehicle.make || 'N/A'}`],
      [`Model: ${active_vehicle.model || 'N/A'}`],
    ];

    doc.text('Vehicle Information', 10, 10);
    autoTable(doc, { startY: 20, body: vehicleInfo });

    const tableData = allItems.map((item) =>
      exportableColumnKeys.map((key) => {
        if (key === 'labor_total') {
          return (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate)).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = toNumber(item.parts) + (toNumber(item.labor_hours) * toNumber(item.labor_rate || default_labor_rate));
          const stored = toNumber(item.estimate_total) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          return item[key] || '';
        }
      })
    );

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [exportableColumnKeys.map(key => key.replace(/_/g, ' ').toUpperCase())],
      body: tableData,
    });

    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  const onPrint = async () => {
    const node = document.getElementById('print-inspection');
    if (!node) return;
    try {
      const dataUrl = await domtoimage.toPng(node);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();
      const imgWidth = width - 30;
      const imgHeight = (imgWidth * node.offsetHeight) / node.offsetWidth;
      pdf.addImage(dataUrl, 'PNG', 15, 15, imgWidth, imgHeight);
      pdf.save(`inspection_${active_vehicle.stock}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    }
  };

  return (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Stack>
        <Typography variant="h5">
          {active_vehicle.year} {active_vehicle.make} {active_vehicle.model}
        </Typography>
        <Typography variant="caption">
          Stock: {active_vehicle.stock} | VIN: {active_vehicle.vin}
        </Typography>
      </Stack>
      <Box className="no-print">
        <Button
          variant="contained"
          disabled={Object.values(groupedItems).flat().length === 0}
          onClick={handleExportDataToCSV}
          startIcon={<FileDownload />}
          sx={{ mr: 1 }}
        >
          Export CSV
        </Button>
        <Button
          variant="contained"
          disabled={Object.values(groupedItems).flat().length === 0}
          onClick={handleExportDataToPDF}
          startIcon={<FileDownload />}
          sx={{ mr: 1 }}
        >
          Export PDF
        </Button>
        <Button
          variant="contained"
          disabled={Object.values(groupedItems).flat().length === 0}
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          sx={{ mr: 1 }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setFlyoutOpen(true)}
        >
          Create Multiple Work Items
        </Button>
      </Box>
    </Box>
  );
};

export default VehicleHeader;